import React, { useMemo, useState } from 'react';

import { CustomAccordion as Accordion } from '@planne-software/uni/Accordion';

import UniTheme from '@src/common/ui/UniTheme';
import { useAppDispatch, useWidgetsTheme } from '@src/core/hooks';
import useTranslate from '@src/i18n/useTranslate';

import {
  AccordionContent,
  AccordionWrapper,
  CartProduct,
  Container,
  InformationLabel,
  InformationValue,
  ProductImage,
  ProductInformation,
  ProductInformationContainer,
  ProductName,
  TariffContainer,
  TariffList,
} from './styles';
import { OutlinedButton } from '@src/components/reusable';
import ErrorWarning from '../ErrorWarning';
import CartItemContainer from '.';
import TotalPriceSummary from './TotalPriceSummary';
import { removeCartPackage } from '@src/features/cart/store/cart';
import { calculatePackageTotalPriceSummary } from '@src/domains/cart';
import { formatItemSchedule } from '@src/common/utils/formatItemSchedule';
import { formatCartItemPackageTariffs } from '@src/features/cart/core/formatCartItemPackageTariffs';
import { getImageUrl } from '@src/common/utils/image';
import { CartItem, ProductPackage } from '@src/core/buyerApiClient';
import { useShowItemPrices } from '@src/hooks/useShowItemPrices';

interface CartPackageItemContainerProps {
  packageItem: ProductPackage;
  items: CartItem[];
  editable?: boolean;
  showErrors?: boolean;
}

const CartPackageItemContainer = (props: Readonly<CartPackageItemContainerProps>) => {
  const { packageItem, items, editable = true, showErrors = true } = props;

  const [removeItemError, setRemoveItemError] = useState(false);
  const [disabledItem, setDisabledItem] = useState(false);
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();

  const removeItem = () => {
    setDisabledItem(true);
    setRemoveItemError(false);

    try {
      dispatch(removeCartPackage(items[0]!.productPackageUnitId!));
    } catch (error) {
      setRemoveItemError(true);
    } finally {
      setDisabledItem(false);
    }
  };

  const mainColor = useWidgetsTheme('actionColor');

  const selectedStringDates = useMemo(
    () =>
      items
        .map((item) => formatItemSchedule(item, { fallbackText: i18n('account_sale_item_without_date') }))
        .join(', '),
    [items, i18n],
  );

  const selectedTariffsMap = useMemo(() => formatCartItemPackageTariffs(items), [items]);

  const { total, discount, previousPrice } = useMemo(() => calculatePackageTotalPriceSummary(items), [items]);

  const showItemPrices = useShowItemPrices();

  const hasPackageItemErrors = useMemo(() => {
    return items.some((item) => item.errors && item.errors.length > 0);
  }, [items]);

  const imageUrl = useMemo(() => getImageUrl(packageItem.image), [packageItem.image]);

  return (
    <Container>
      <CartProduct>
        <ProductImage hidden={!imageUrl}>
          <img src={imageUrl ?? ''}></img>
        </ProductImage>

        <ProductInformationContainer>
          <ProductName>{packageItem.name}</ProductName>

          <ProductInformation>
            <InformationLabel>{i18n('cart_selectedDate')}:</InformationLabel>

            <InformationValue>
              <span>{selectedStringDates}</span>
            </InformationValue>
          </ProductInformation>

          <TariffContainer>
            <InformationLabel>{i18n('cart_selectedTariff')}:</InformationLabel>

            <TariffList>
              {Object.entries(selectedTariffsMap).map(([name, quantity]) => (
                <InformationValue key={name}>
                  {name} - {quantity}
                </InformationValue>
              ))}
            </TariffList>
          </TariffContainer>
        </ProductInformationContainer>
      </CartProduct>

      <UniTheme>
        <AccordionWrapper>
          <Accordion
            title={i18n('cart_package_details')}
            content={
              <AccordionContent aria-label='accordion-content'>
                {items.map((item) => (
                  <CartItemContainer key={item.id} item={item} editable={false} showErrors={showErrors} />
                ))}
              </AccordionContent>
            }
          />
        </AccordionWrapper>
      </UniTheme>

      {showItemPrices ? <TotalPriceSummary total={total} discount={discount} previousPrice={previousPrice} /> : null}

      {hasPackageItemErrors && showErrors ? <ErrorWarning>{i18n('cart_package_error')}</ErrorWarning> : null}

      {editable ? (
        <OutlinedButton onClick={removeItem} color={mainColor} disabled={disabledItem} showLoadingOnDisabled>
          {i18n('cart_cancel')}
        </OutlinedButton>
      ) : null}

      {removeItemError ? <ErrorWarning>{i18n('cart_item_remove_error')}</ErrorWarning> : null}
    </Container>
  );
};

export default CartPackageItemContainer;
